import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { showModal } from 'src/redux/actions/modalActions';
import Header from 'src/layouts/Header';
import Footer from 'src/layouts/Footer';

const ProtectedRoute = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useSelector((state) => state.auth);

  useEffect(() => {
    // If user is not logged in, save current location and show login modal
    if (!auth.isLoggedIn) {
      // Save current location in localStorage so user can be redirected after login
      localStorage.setItem('redirectAfterLogin', location.pathname + location.search);
      dispatch(showModal('LOGIN_MODAL'));
    } else {
      // If user is logged in, check if there's a saved location and redirect
      const redirectPath = localStorage.getItem('redirectAfterLogin');
      if (redirectPath && redirectPath !== location.pathname + location.search) {
        localStorage.removeItem('redirectAfterLogin');
        navigate(redirectPath);
      }
    }
  }, [auth.isLoggedIn, dispatch, location, navigate]);

  // If user is not logged in, show professional authentication page
  if (!auth.isLoggedIn) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <div className="bg-[#F7F7F7] flex-1 flex items-center justify-center py-4">
          <div className="container mx-auto px-4 w-full flex justify-center">
            <div className="max-w-[600px] w-full py-[15px] md:py-[25px] bg-white border border-[#EBEBEB] rounded-lg" style={{ boxShadow: '0px 3px 6px #8D8D8D36' }}>
              <div className="px-4 md:px-6 lg:px-10">
                {/* Icon */}
                <div className="flex justify-center mb-6 mt-4">
                  <div className="w-16 h-16 bg-[#832EE6] rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                </div>

                {/* Title */}
                <h1 className="text-[24px] md:text-[32px] font-bold text-center mb-3 text-[#0D0D0D]">
                  Authentication Required
                </h1>
                
                {/* Description */}
                <p className="text-gray-600 text-center mb-8 leading-relaxed text-sm md:text-base">
                  This page requires you to be logged in. Please sign in to your account to continue.
                </p>

                {/* Features list */}
                <div className="space-y-3 mb-8">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-[#832EE6] mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700 text-sm">Access your personal dashboard</span>
                  </div>
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-[#832EE6] mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700 text-sm">Manage your ads and favorites</span>
                  </div>
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-[#832EE6] mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700 text-sm">Secure and private access</span>
                  </div>
                </div>

                {/* Login button */}
                <button
                  onClick={() => dispatch(showModal('LOGIN_MODAL'))}
                  className="w-full bg-[#832EE6] text-white font-bold py-3 px-6 rounded-full hover:bg-[#6f26c4] transition-colors mb-4 text-base md:text-lg"
                >
                  Sign In to Continue
                </button>

                {/* Help text */}
                <p className="text-xs text-gray-500 text-center mb-4">
                  A login window should appear automatically. If not, click the button above.
                </p>

                {/* Divider */}
                <hr className="my-6 border-[#EBEBEB]" />

                {/* Additional info */}
                <div className="text-center">
                  <p className="text-sm text-gray-600">
                    Don't have an account?{' '}
                    <button
                      onClick={() => dispatch(showModal('REGISTER_MODAL'))}
                      className="text-[#832EE6] font-bold hover:text-[#6f26c4] transition-colors"
                    >
                      Sign up here
                    </button>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // If user is logged in, render children
  return children;
};

export default ProtectedRoute;

